import { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronsUpDown, LogOut } from 'lucide-react';

import { initials, shortName } from '@/shared/lib/format';
import { cn } from '@/shared/lib/utils';

interface UserMenuProps {
  fullName: string;
  /** `Líder · Coordenador` — os papéis já formatados por quem chamou. */
  roleLabel: string;
  /** Rail estreito: só o avatar cabe, e o nome vira `title`. */
  collapsed?: boolean;
  /** `up` no rodapé da sidebar, `down` na faixa superior do mobile. */
  direction?: 'up' | 'down';
  onSignOut: () => void;
  className?: string;
}

/**
 * Quem está logado + a única ação de conta que existe hoje: sair.
 *
 * Escrito à mão em vez do `DropdownMenu` do shadcn/ui porque aquele traz
 * `@radix-ui/react-dropdown-menu` junto, e dependência nova exige ADR
 * (regra 13). Um menu de um item precisa fechar no clique fora, no `Esc` e ao
 * escolher — é exatamente o que está aqui, e nada além.
 *
 * Vive em `app/` porque nasce da sessão: `shared/` não conhece quem está
 * logado (ADR-006).
 */
export function UserMenu({
  fullName,
  roleLabel,
  collapsed = false,
  direction = 'up',
  onSignOut,
  className,
}: UserMenuProps) {
  const [open, setOpen] = useState(false);
  /**
   * Um menu que abre suave e some no talo parece quebrado — mas animar a SAÍDA
   * exige manter o nó vivo depois de `open` virar `false`. É só isso que este
   * segundo estado faz: `isClosing` segura o menu no DOM até a animação
   * terminar, e o `onAnimationEnd` o desmonta.
   *
   * É o que o Radix faz por baixo do pano no `DropdownMenu` do shadcn/ui. Aqui
   * são seis linhas em vez de uma dependência nova (regra 13).
   */
  const [isClosing, setIsClosing] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const close = useCallback(() => {
    setOpen((wasOpen) => {
      if (wasOpen) setIsClosing(true);
      return false;
    });
  }, []);

  useEffect(() => {
    if (!open) return;

    function handlePointerDown(event: MouseEvent | TouchEvent) {
      if (!containerRef.current?.contains(event.target as Node)) close();
    }

    // Devolver o foco ao gatilho é o que fecha o ciclo para teclado: sem isso
    // o `Esc` deixa o foco no nada e o próximo `Tab` recomeça do topo da tela.
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key !== 'Escape') return;
      close();
      triggerRef.current?.focus();
    }

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, close]);

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => {
          if (open) close();
          else {
            setIsClosing(false);
            setOpen(true);
          }
        }}
        aria-haspopup="menu"
        aria-expanded={open}
        title={collapsed ? `${fullName} — ${roleLabel}` : undefined}
        className={cn(
          'group flex w-full items-center gap-2.5 rounded-md py-2 text-left',
          'transition-[color,background-color,transform] duration-fast ease-out active:scale-[0.98]',
          'hover:bg-accent focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
          collapsed ? 'justify-center px-0' : 'px-2',
          open && 'bg-accent',
        )}
      >
        <span
          aria-hidden="true"
          className="flex size-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary transition-transform duration-fast ease-out group-hover:scale-105"
        >
          {initials(fullName)}
        </span>

        {!collapsed ? (
          <>
            <span className="min-w-0 flex-1 leading-tight">
              <span className="block truncate text-sm font-medium text-foreground">
                {shortName(fullName)}
              </span>
              <span className="block truncate text-xs text-muted-foreground">{roleLabel}</span>
            </span>
            <ChevronsUpDown
              className={cn(
                'size-4 shrink-0 text-muted-foreground transition-transform duration-base ease-out',
                open && 'rotate-180',
              )}
              aria-hidden="true"
            />
          </>
        ) : (
          <span className="sr-only">{fullName}</span>
        )}
      </button>

      {open || isClosing ? (
        <div
          role="menu"
          aria-label="Conta"
          onAnimationEnd={() => setIsClosing(false)}
          className={cn(
            'absolute z-40 min-w-[200px] overflow-hidden rounded-lg border border-border bg-popover p-1 shadow-lg',
            open ? 'animate-menu-in' : 'animate-menu-out',
            // O menu CRESCE do canto em que o gatilho está — é o que amarra o
            // painel ao botão em vez de fazê-lo brotar solto na tela. O canto
            // tem de acompanhar a mesma lógica de posicionamento de baixo.
            direction === 'up'
              ? collapsed
                ? 'origin-bottom-left'
                : 'origin-bottom'
              : 'origin-top-right',
            // De onde o menu CRESCE muda com o canto em que o gatilho está: no
            // rodapé da sidebar ele abre para cima e para a direita (no rail de
            // 68px, para dentro do conteúdo); na faixa superior do mobile o
            // gatilho encosta na borda direita e o menu tem de crescer para a
            // esquerda, ou sai da tela.
            direction === 'up' ? 'bottom-full left-0 mb-2' : 'right-0 top-full mt-2',
            direction === 'up' && !collapsed && 'right-0',
          )}
        >
          {/* No rail estreito o nome não cabe no gatilho; o cabeçalho do menu é
              onde a pessoa confirma em qual conta está antes de sair. */}
          {collapsed ? (
            <div className="border-b border-border px-2.5 pb-2 pt-1.5 leading-tight">
              <p className="truncate text-sm font-medium text-foreground">{shortName(fullName)}</p>
              <p className="truncate text-xs text-muted-foreground">{roleLabel}</p>
            </div>
          ) : null}

          <button
            type="button"
            role="menuitem"
            onClick={() => {
              close();
              onSignOut();
            }}
            className={cn(
              'flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-sm text-foreground',
              'transition-colors duration-fast ease-out',
              'hover:bg-accent focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-ring',
              collapsed && 'mt-1',
            )}
          >
            <LogOut className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
            Sair do sistema
          </button>
        </div>
      ) : null}
    </div>
  );
}
